import { useEffect, useState, useCallback, useMemo } from "react";
import { Polygon, Polyline, CircleMarker } from "react-leaflet";
import LeafletMap, { FitBounds, ZONE_PALETTE } from "@/components/map/LeafletMap";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Check, RotateCcw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, scoreColor } from "@/lib/prospects";
import BusinessLayers from "@/components/map/BusinessLayers";
import HeatLayer from "@/components/map/HeatLayer";
import { useBusinessDiscovery } from "@/lib/useBusinessDiscovery";
import { BusinessLayerToggles } from "@/components/map/BusinessLayerToggles";

// Create a zone from TWO lines ("cuts") drawn across the bike-path corridor. The
// band between the two lines becomes a new AVAILABLE zone. Cuts are allowed anywhere
// (overlapping any existing zone, on any bike path, blocks creation). Four clicks = line1 (2 pts) + line2 (2 pts).
export default function ZoneTwoLineCreate({ bikePath, zones = [], nearbyZones = [], open, onOpenChange, onDone }) {
  const [pts, setPts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) { setPts([]); setPreview(null); setBusy(false); setCreating(false); }
  }, [open]);

  const boundary = bikePath?.boundary || [];

  // Business Discovery (same data the big Zones map shows) for this corridor.
  const biz = useBusinessDiscovery(boundary, { bikePathId: bikePath?.id });

  // Snap candidates: corridor edges + every existing zone border, so new zones sit
  // flush (no slivers). Clicks snap to the nearest vertex, else onto the nearest edge.
  const snapData = useMemo(() => {
    const rings = [];
    if (boundary.length >= 3) rings.push(boundary);
    [...(zones || []), ...(nearbyZones || [])].forEach((z) => { if ((z.polygon || []).length >= 3) rings.push(z.polygon); });
    const verts = [], segs = [];
    rings.forEach((r) => { for (let i = 0; i < r.length; i++) { verts.push(r[i]); segs.push([r[i], r[(i + 1) % r.length]]); } });
    let thr = 0.0015;
    if (boundary.length) {
      const lats = boundary.map((p) => p[0]), lngs = boundary.map((p) => p[1]);
      thr = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs)) * 0.03;
    }
    return { verts, segs, thr };
  }, [boundary, zones, nearbyZones]);

  const snap = (lat, lng) => {
    const { verts, segs, thr } = snapData;
    let best = null, bd = thr;
    for (const v of verts) { const d = Math.hypot(v[0] - lat, v[1] - lng); if (d < bd) { bd = d; best = [v[0], v[1]]; } }
    if (best) return best;
    bd = thr;
    for (const [a, b] of segs) {
      const [y1, x1] = a, [y2, x2] = b, dy = y2 - y1, dx = x2 - x1, len2 = dy * dy + dx * dx;
      if (len2 === 0) continue;
      let t = ((lat - y1) * dy + (lng - x1) * dx) / len2;
      t = Math.max(0, Math.min(1, t));
      const p = [y1 + t * dy, x1 + t * dx];
      const d = Math.hypot(p[0] - lat, p[1] - lng);
      if (d < bd) { bd = d; best = p; }
    }
    return best || [lat, lng];
  };

  const runPreview = useCallback(async (p) => {
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post(`/bike-paths/${bikePath.id}/zone-from-lines/preview`, {
        l1: [p[0], p[1]], l2: [p[2], p[3]],
      });
      setPreview(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setPts([]); }
    finally { setBusy(false); }
  }, [bikePath?.id]);

  const onMapClick = (lat, lng) => {
    if (busy) return;
    const s = snap(lat, lng);
    setPts((cur) => {
      const next = (cur.length >= 4 ? [] : cur).concat([s]);
      if (next.length === 4) runPreview(next);
      return next;
    });
  };

  const seg = (a, b) => {
    if (!a || !b) return null;
    const [y1, x1] = a, [y2, x2] = b;
    const dx = x2 - x1, dy = y2 - y1, ext = 1.2;
    return [[y1 - dy * ext, x1 - dx * ext], [y2 + dy * ext, x2 + dx * ext]];
  };

  const confirm = async () => {
    setCreating(true);
    try {
      await api.post(`/bike-paths/${bikePath.id}/zone-from-lines`, { l1: [pts[0], pts[1]], l2: [pts[2], pts[3]] });
      toast.success(`Zone ${preview.zoneNumber} created — rename it any time`);
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const reset = () => { setPts([]); setPreview(null); };

  const prompt = busy ? "Computing…"
    : preview ? "Review the new zone below, then create it."
    : pts.length === 0 ? "Cut 1 — click one side of the corridor."
    : pts.length === 1 ? "Cut 1 — click the opposite side."
    : pts.length === 2 ? "Cut 2 — click one side of the corridor."
    : pts.length === 3 ? "Cut 2 — click the opposite side." : "Computing…";

  const line1 = seg(pts[0], pts[1]);
  const line2 = seg(pts[2], pts[3]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[92vw] xl:max-w-7xl max-h-[86vh] top-[62%] overflow-y-auto" data-testid="zone-two-line-dialog">
        <DialogHeader>
          <DialogTitle>New zone — two cuts</DialogTitle>
          <DialogDescription data-testid="two-line-prompt">{prompt}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="zone-two-line-map">
          <div className="relative rounded-lg overflow-hidden border border-border">
          <LeafletMap height="52vh" onClick={onMapClick} center={boundary.length ? boundary[0] : undefined}>
            {boundary.length >= 2 && <FitBounds bounds={boundary} nonce={open ? 1 : 0} />}
            {boundary.length >= 3 && <Polygon positions={boundary} pathOptions={{ color: "#64748B", weight: 2, fillOpacity: 0.05, dashArray: "6", interactive: false }} />}
            {(zones || []).map((z, i) => ((z.polygon || []).length >= 3 ? <Polygon key={`nz-${i}`} positions={z.polygon} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.12, weight: 1.5, interactive: false }} /> : null))}
            {(nearbyZones || []).map((z) => ((z.polygon || []).length >= 3 ? <Polygon key={`nb-${z.id}`} positions={z.polygon} pathOptions={{ color: "#A855F7", fillColor: "#A855F7", fillOpacity: 0.15, weight: 2, dashArray: "5 4", interactive: false }} /> : null))}
            {preview?.polygon?.length >= 3 && <Polygon positions={preview.polygon} pathOptions={{ color: ZONE_PALETTE[0], fillColor: ZONE_PALETTE[0], fillOpacity: 0.35, weight: 2, interactive: false }} />}
            {line1 && <Polyline positions={line1} pathOptions={{ color: "#EF4444", weight: 3, dashArray: "6", interactive: false }} />}
            {line2 && <Polyline positions={line2} pathOptions={{ color: "#F59E0B", weight: 3, dashArray: "6", interactive: false }} />}
            {pts.map((p, i) => <CircleMarker key={i} center={p} radius={6} pathOptions={{ color: "#fff", fillColor: i < 2 ? "#EF4444" : "#F59E0B", fillOpacity: 1, weight: 2 }} />)}
            {biz.visibleBiz.length > 0 && <BusinessLayers businesses={biz.visibleBiz} onShopAction={biz.handleShopAction} />}
            {biz.heatOn && <HeatLayer points={biz.heatPoints} enabled={biz.heatOn} />}
          </LeafletMap>
          {boundary.length >= 3 && <BusinessLayerToggles {...biz} />}
          </div>
          {nearbyZones.length > 0 && (
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5" data-testid="two-line-nearby-legend">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(168,85,247,0.25)", border: "1px dashed #A855F7" }} />
              Purple dashed = zones on other bike paths ({nearbyZones.map((z) => `${z.name}${z.bikePath ? ` · ${z.bikePath}` : ""}`).join(", ")}). Your new zone can't overlap them.
            </div>
          )}

          {busy && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Computing zone…</div>}

          {preview && (
            <div className="rounded-xl border p-3 bg-card" style={{ borderColor: ZONE_PALETTE[0] }} data-testid="two-line-preview-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading font-bold tabular-nums" style={{ color: ZONE_PALETTE[0] }}>Zone {preview.zoneNumber} (new)</span>
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">auto-named — rename later</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div><div className="text-muted-foreground">Businesses</div><div className="tabular-nums font-medium" data-testid="two-line-biz">{preview.businessCount}</div></div>
                <div><div className="text-muted-foreground">Prospects</div><div className="tabular-nums font-medium" data-testid="two-line-prospects">{preview.prospectCount}</div></div>
                <div><div className="text-muted-foreground">Est $/mo</div><div className="tabular-nums text-emerald-500 font-medium" data-testid="two-line-rev">{fmtMoney(preview.projectedMonthly)}</div></div>
                <div><div className="text-muted-foreground">Opportunity</div><div className={`tabular-nums font-bold ${scoreColor(preview.opportunityScore)}`} data-testid="two-line-opp">{preview.opportunityScore}</div></div>
              </div>
              {preview.overlaps?.length > 0 && (
                <div className="mt-2 text-xs text-destructive flex items-center gap-1.5" data-testid="two-line-overlap-warn">
                  <TriangleAlert className="w-3.5 h-3.5 shrink-0" />Overlaps existing zone {preview.overlaps.join(", ")} — zones can't overlap. Move your cut lines.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-2 border-t border-border/50 z-10">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="two-line-cancel">Cancel</Button>
          {(pts.length > 0 || preview) && <Button variant="outline" onClick={reset} data-testid="two-line-reset"><RotateCcw className="w-4 h-4 mr-1" />Reset cuts</Button>}
          <Button onClick={confirm} disabled={!preview || creating || preview.overlaps?.length > 0} data-testid="two-line-confirm">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Create zone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
